import {
  AppShell,
  CompanySwitcher,
  getThemePreference,
  SidebarBack,
  type SidebarMode,
  setThemePreference,
  type ThemeChoice,
  usePersistentFlag,
} from "@majitap/ui";
import { ToastStackProvider } from "@majitap/ui/arc/toast-stack/toast-stack";
import { UserMenu } from "@majitap/ui/arc/user-menu/user-menu";
import { useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { UserRound } from "lucide-react";
import { useState } from "react";
import { countsQuery, withCounts } from "../counts";
import { CrumbProvider, useCrumbState } from "../crumb";
import { adminCompanies } from "../guards";
import {
  ACCOUNT_ITEM,
  allowed,
  breadcrumbFor,
  itemAt,
  MAIN_MENU,
  SETTINGS_ITEM,
  SETTINGS_MENU,
  toNavItem,
  toNavSections,
  visibleMenu,
} from "../nav";
import { chooseCompany, meQuery } from "../session";
import { useSignOut } from "../use-sign-out";
import { ConsoleNotifications, NotificationToasts } from "./notifications";
import { ConsoleSearch } from "./search";

const COLLAPSED_KEY = "powwater.console.sidebar-collapsed";

/**
 * Every signed-in page renders here: the App Shell with the menu the role may see. Under
 * /settings the sidebar swaps to the Settings menu (spec rule 02).
 */
export function ConsoleLayout() {
  const { data: me } = useSuspenseQuery(meQuery);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { signOut } = useSignOut();
  const [collapsed, setCollapsed] = usePersistentFlag(COLLAPSED_KEY, false);
  const [recordCrumb, setRecordCrumb] = useCrumbState();
  const counts = useQuery({ ...countsQuery(), enabled: Boolean(me?.active_company) });
  const [theme, setTheme] = useState<ThemeChoice>(getThemePreference);
  if (!me?.active_company) return null;

  const permissions = me.permissions;
  const inSettings = pathname === "/settings" || pathname.startsWith("/settings/");
  const menu = visibleMenu(inSettings ? SETTINGS_MENU : MAIN_MENU, permissions);
  const sections = inSettings ? toNavSections(menu) : withCounts(toNavSections(menu), counts.data);
  const footerItems = !inSettings && allowed(SETTINGS_ITEM.gate, permissions) ? [toNavItem(SETTINGS_ITEM)] : [];
  const active = itemAt(pathname);

  // switching company reopens the console there, with nothing cached from the old one
  const switchCompany = async (companyId: string) => {
    const updated = await chooseCompany(companyId);
    queryClient.clear();
    queryClient.setQueryData(meQuery.queryKey, updated);
    await navigate({ to: "/" });
  };

  const header = (mode: SidebarMode) =>
    inSettings ? (
      <SidebarBack href="/" label="Back to console" title="Settings" compact={mode === "rail"} />
    ) : (
      <CompanySwitcher
        companies={adminCompanies(me).map((c) =>
          c.id === me.active_company?.id ? { ...c, logoSrc: me.active_company.logo_url } : c,
        )}
        currentId={me.active_company!.id}
        product="Admin console"
        onSwitch={switchCompany}
        compact={mode === "rail"}
      />
    );

  const user = (mode: SidebarMode) => (
    <UserMenu
      user={{ name: me.user.name, email: me.user.email ?? "", avatarSrc: me.user.image_url ?? undefined }}
      showName={mode === "expanded"}
      items={[
        { label: ACCOUNT_ITEM.label, icon: <UserRound size={16} />, onSelect: () => navigate({ to: "/account" }) },
      ]}
      theme={theme}
      onThemeChange={(choice) => {
        setTheme(choice);
        setThemePreference(choice);
      }}
      align="start"
      onSignOut={signOut}
    />
  );

  return (
    <ToastStackProvider>
      <AppShell
        header={header}
        sections={sections}
        footerItems={footerItems}
        user={user}
        activeId={active?.item.id}
        breadcrumb={breadcrumbFor(pathname, recordCrumb)}
        collapsed={collapsed}
        onCollapsedChange={setCollapsed}
        navLabel={inSettings ? "Settings menu" : "Main menu"}
        topBarActions={
          <>
            <ConsoleSearch
              menu={visibleMenu([...MAIN_MENU, ...SETTINGS_MENU], permissions)}
              permissions={permissions}
            />
            <ConsoleNotifications />
          </>
        }
      >
        <CrumbProvider onChange={setRecordCrumb}>
          <Outlet />
        </CrumbProvider>
      </AppShell>
      {/* per company: switching starts afresh, so its older notifications aren't toasted */}
      <NotificationToasts key={me.active_company.id} />
    </ToastStackProvider>
  );
}
